// ═══════════════════════════════════════════════════════════════════════════════
// Public legal / compliance pages (Privacy, Terms, PICS)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveLegalDocumentLocale, usesEnglishLegalContent, type LegalDocumentPublic, type LegalDocumentType } from '@fdh/shared';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { InfoPageShell } from '../../components/layout/InfoPageShell';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { legalDocumentApi } from '../../lib/api';
import { APP_ICONS } from '../../lib/iconAssets';

type LegalVariant = Exclude<LegalDocumentType, 'subscription_terms'>;

const renderLegalContent = (content: string) =>
  content.split('\n').map((line, index) => {
    const key = `${index}-${line.slice(0, 12)}`;
    if (line.trim() === '') {
      return <div key={key} className="h-3" aria-hidden />;
    }
    if (line.startsWith('### ')) {
      return (
        <h2 key={key} className="mt-5 text-base font-semibold text-gray-900 first:mt-0">
          {line.replace(/^###\s+/, '')}
        </h2>
      );
    }
    if (/^\d+\.\s/.test(line)) {
      return (
        <p key={key} className="pl-4 text-sm leading-relaxed text-gray-700">
          {line}
        </p>
      );
    }
    return (
      <p key={key} className="text-sm leading-relaxed text-gray-700">
        {line}
      </p>
    );
  });

export const LegalPage = ({ variant }: { variant: LegalVariant }) => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [document, setDocument] = useState<LegalDocumentPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const titleKey = `legal.${variant}.title`;
  const bodyKey = `legal.${variant}.body`;

  const showEnglishContentNotice = usesEnglishLegalContent(language);

  useEffect(() => {
    const loadDocument = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await legalDocumentApi.getActive(variant, resolveLegalDocumentLocale(language));
        if (response.success && response.data) {
          setDocument(response.data);
          return;
        }
        setError(response.error ?? t('auth.errorGeneric'));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setLoading(false);
      }
    };
    void loadDocument();
  }, [language, t, variant]);

  const title = document?.title ?? t(titleKey);
  const content = document?.content ?? t(bodyKey);

  const pageContent = (
    <div
      className={
        isAuthenticated
          ? 'min-h-full bg-[#f7f8fb] px-4 py-6 dark:bg-gray-950'
          : 'min-h-full bg-gray-50 px-4 py-8 pb-16'
      }
    >
      <div className="mx-auto max-w-2xl">
        {!isAuthenticated ? (
          <>
            <div className="mb-6 flex items-center justify-between gap-2">
              <Link
                to="/"
                className="text-sm font-medium text-primary-600 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                ← {t('legal.backHome')}
              </Link>
              <LanguageSwitcher />
            </div>
            <span className="flex items-center gap-2 mb-4">
              <img
                src={APP_ICONS.any192}
                alt="MaidEasy logo"
                className="mb-4 h-14 w-14 rounded-lg object-contain"
              /> 
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">MaidEasy - Make.Easy!</h1> <br/>
            </span>
          </>
        ) : null}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
        {showEnglishContentNotice ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
            {t('legal.contentEnglishNotice')}
          </p>
        ) : null}
        {document ? (
          <p className="mt-1 text-xs text-gray-500">
            Version: {document.version}
          </p>
        ) : null}
        {loading ? <p className="mt-6 text-sm text-gray-500">{t('common.loading')}</p> : null}
        {error ? (
          <p className="mt-6 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}
        {!loading ? (
          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            {renderLegalContent(content)}
          </div>
        ) : null}
      </div>
    </div>
  );

  return <InfoPageShell>{pageContent}</InfoPageShell>;
};
